import React, { createContext, useContext, useState, useEffect } from 'react'
import authService from '../services/authService'
import { User } from '../types/admin'

interface AuthContextType {
  currentUser: User | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    const checkAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const user = await authService.fetchCurrentUser()
          setCurrentUser(user)
          setIsAuthenticated(true)
        } catch (error) {
          setCurrentUser(null)
          setIsAuthenticated(false)
        }
      }
    }
    checkAuth()
  }, [])

  const login = async (email: string, password: string) => {
    const response = await authService.login(email, password)
    setCurrentUser(response.user)
    setIsAuthenticated(true)
  }

  const logout = async () => {
    await authService.logout()
    setCurrentUser(null)
    setIsAuthenticated(false)
  }

  return (
    <AuthContext.Provider
      value={{ currentUser, isAuthenticated, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
